import { canPlayManual3D } from "../../common/manual3D.ts";
import { PHASE } from '../../common/constants.ts';
import { idb } from '../db/index.ts';
import { g, lock } from '../util/index.ts';
import { season, team, phase } from '../core/index.ts';
import play from '../core/game/play.ts';
import loadTeams from '../core/game/loadTeams.ts';
import GameSim from '../core/GameSim.baseball/index.ts';
import type { Conditions } from '../../common/types.ts';

// Use ZenGM's own aggregation and formulas, including all teams after a trade.
export const getManualSeasonStats = async (players: any[], seasonNumber: number) => idb.getCopies.playersPlus(players, {
 attrs: ['pid','jerseyNumber','age'], ratings: ['ovr','pot'], fuzz: true, showRookies: true,
 stats: ['gp','pa','ab','h','ba','obp','slg','ops','hr','rbi','sb','bb','so','gpPit','gsPit','w','l','sv','outs','ip','era','whip','soPit','bbPit','hPit','er'],
 season: seasonNumber, regularSeason: true, playoffs: false, mergeStats: 'totOnly', showNoStats: true,
});

export const getManualCareerStats = async (players: any[], seasonNumber: number) => idb.getCopies.playersPlus(players.map(p=>({...p,stats:p.stats.filter((row:any)=>row.season<=seasonNumber)})), {
 attrs: ['pid'],
 stats: ['gp','pa','ab','h','ba','obp','slg','ops','hr','rbi','sb','bb','so','gpPit','gsPit','w','l','sv','outs','ip','era','whip','soPit','bbPit','hPit','er'],
 regularSeason: true, playoffs: false, mergeStats: 'totOnly', showNoStats: true,
});

import { manualGames } from '../core/game/manualResult.ts';
const dhFor = (t: any) => { const dh = g.get('dh'); return dh === 'all' || (Array.isArray(dh) && dh.includes(t.cid)); };
export const manual3DStart = async (selection: { gid: number } | undefined, conditions: Conditions) => {
 if (lock.get('gameSim') || lock.get('newPhase')) throw new Error('Espere a simulação terminar.');
 if (!selection && g.get('phase') === PHASE.PRESEASON) await phase.newPhase(PHASE.REGULAR_SEASON, conditions);
 if (![PHASE.REGULAR_SEASON,PHASE.AFTER_TRADE_DEADLINE,PHASE.PLAYOFFS].includes(g.get('phase') as any)) throw new Error('Avance no menu Play até a temporada regular para jogar.');
 const schedule = await season.getSchedule(true);
 const game = selection
  ? schedule.find(x => x.gid === selection.gid)
  : schedule.find(x => x.homeTid === g.get('userTid') || x.awayTid === g.get('userTid'));
 if (!game) throw new Error('Essa partida não está disponível hoje. Atualize o Daily Schedule.');
 if (!canPlayManual3D({ ...game, userTid: g.get('userTid'), phase: g.get('phase'), spectator: g.get('spectator') }))
  throw new Error('Só é possível jogar em 3D as partidas do time que você está comandando.');
 const error=await team.checkRosterSizes('user');
 if(error) {
  const roster=await idb.cache.players.indexGetAll('playersByTid',g.get('userTid'));
  const maximum=g.get('maxRosterSize'),minimum=g.get('minRosterSize');
  throw new Error(roster.length>maximum?`Seu elenco tem ${roster.length} jogadores e o limite é ${maximum}. Use Minor League para enviar ${roster.length-maximum} jogadores às menores e tente novamente.`:roster.length<minimum?`Seu elenco tem ${roster.length} jogadores e precisa de pelo menos ${minimum}. Promova jogadores da Minor League ou contrate jogadores antes de entrar.`:'Ajuste o elenco no ZenGM antes de entrar na partida.');
 }
 await team.checkRosterSizes('other');
 const loaded=await loadTeams([game.homeTid,game.awayTid],conditions);
 const teams:any=[loaded[game.homeTid],loaded[game.awayTid]];
 const dh=dhFor(teams[0]);
 for(const t of teams) t.depth=team.getDepthPlayers(t.depth,t.player,dh);
 const sim=new GameSim({gid:game.gid,day:game.day,teams,dh,doPlayByPlay:false,homeCourtFactor:1,neutralSite:false,allStarGame:false,baseInjuryRate:0});
 const output=[];
 for(const wrapper of sim.team) {
  const t=wrapper.t;
  const dbTeam=await idb.cache.teams.get(t.id);
  const originals=await Promise.all(t.player.map((p:any)=>idb.cache.players.get(p.id)));
  const summaries=await getManualSeasonStats(originals.filter(Boolean),g.get('season'));
  const byPid=new Map(summaries.map(p=>[p.pid,p]));
  const careers=await getManualCareerStats(originals.filter(Boolean),g.get('season'));
  const careerByPid=new Map(careers.map(p=>[p.pid,p.careerStats]));
  const players=[];
  for(let i=0;i<t.player.length;i++) {
   const p=t.player[i]!,original=originals[i],summary=byPid.get(p.id);
   players.push({id:p.id,name:p.name,age:p.age,displayRatings:summary?.ratings,lastName:original?.lastName,jerseyNumber:summary?.jerseyNumber??original?.jerseyNumber??original?.stats.at(-1)?.jerseyNumber,
    bats:(original as any)?.bats,throws:(original as any)?.throws,careerStats:careerByPid.get(p.id)??null,
    pitchingFatigue:p.pFatigue??0,appearance:{subIndex:p.subIndex,order:p.battingOrder,position:p.lineupPos},pos:p.pos,ratings:original?.ratings.at(-1),injured:p.injured,stat:p.stat,seasonStats:summary?.stats??null});
  }
  const active=new Set(Object.values(wrapper.playersInGameByPos).map((v:any)=>v.p.id));
  const bench=t.depth[dh?'D':'DP'].filter((p:any)=>!active.has(p.id)&&!p.injured&&!['SP','RP'].includes(p.pos)).slice(0,5).map((p:any)=>p.id);
  const bullpen=wrapper.depth.pitchers.filter((p:any)=>['SP','RP'].includes(p.pos)).map((p:any)=>p.id);
  output.push({bench,bullpen,id:t.id,name:`${dbTeam?.region} ${dbTeam?.name}`,abbrev:dbTeam?.abbrev,colors:dbTeam?.colors,players,
   lineup:wrapper.playersInGameByBattingOrder.map(p=>p.p.id),pitcher:wrapper.playersInGameByPos.P.p.id,
   defense:Object.fromEntries(Object.entries(wrapper.playersInGameByPos).map(([pos,p]:any)=>[pos,p.p.id]))});
 }
 return {gid:game.gid,lid:g.get('lid'),season:g.get('season'),playoffs:g.get('phase')===PHASE.PLAYOFFS,userTid:g.get('userTid'),teams:output};
};
export const manual3DFinish=async(payload:any,conditions:Conditions)=>{
 if(lock.get('gameSim')) throw new Error('Outra simulação está em andamento.');
 if(payload.lid!==g.get('lid') || payload.season!==g.get('season')) throw new Error('A liga ou temporada mudou. Resultado preservado no aparelho.');
 const schedule=await season.getSchedule(true);
 const game=schedule.find(x=>x.gid===payload.gid);
 if(!game) {
  const existing=await idb.cache.games.get(payload.gid);
  if(existing) return {saved:true,alreadySaved:true};
  throw new Error('A partida não está mais no calendário.');
 }
 if (!canPlayManual3D({ ...game, userTid: g.get('userTid'), phase: g.get('phase'), spectator: g.get('spectator') })) throw new Error('O time controlado mudou. Volte ao time da partida para gravar o resultado.');
 if(!payload.over || payload.score?.length!==2 || payload.score[0]===payload.score[1] || payload.inning<9 || payload.teams[0].id!==game.homeTid || payload.teams[1].id!==game.awayTid) throw new Error('Resultado incompleto.');
 for(let i=0;i<2;i++) {
  const currentPlayers=await idb.cache.players.indexGetAll('playersByTid',payload.teams[i].id);
  for(const [pid,stats] of Object.entries(payload.stats[i]) as any) if(stats.gp>0 && !currentPlayers.some(p=>p.pid===Number(pid))) throw new Error('O elenco mudou durante a partida. Restaure o elenco para gravar este resultado.');
  if(payload.innings[i].reduce((a:number,b:number)=>a+b,0)!==payload.score[i]) throw new Error('Placar inconsistente.');
  for(const stats of Object.values(payload.stats[i]) as any[]) for(const v of Object.values(stats)) {
   if(typeof v==='number' && !Number.isFinite(v)) throw new Error('Estatística inválida.');
  }
 }
 manualGames.set(game.gid,payload);
 try { await play(1,conditions,true,game.gid,false); }
 finally { manualGames.delete(game.gid); }
 const saved=await idb.cache.games.get(game.gid);
 if(!saved) throw new Error('ZenGM não gravou a partida. Confira o elenco e tente novamente.');
 return {saved:true};
};
